import './styles.css';
import { BOWL_SOUNDS, ensureAudioContext, getBowlSound, playBellSignal } from './audio';
import { DEFAULT_PREFERENCES, loadPreferences, savePreferences } from './preferences';
import {
  completeSession,
  createSession,
  formatTime,
  getSnapshot,
  markIntervalPlayed,
  minutesToMs,
  pauseSession,
  resetSession,
  resumeSession,
  shouldPlayInterval,
  startSession,
  updateSessionSettings
} from './timer';
import type { BellSignal, UserPreferences } from './types';

const intervalPresets = [
  { label: '1', minutes: 1 },
  { label: '3', minutes: 3 },
  { label: '5', minutes: 5, isDefault: true },
  { label: '10', minutes: 10 },
  { label: '15', minutes: 15 },
  { label: '25', minutes: 25 },
  { label: '60', minutes: 60 }
];

const durationPresets = [
  { label: 'Open', minutes: null },
  { label: '10', minutes: 10 },
  { label: '20', minutes: 20 },
  { label: '30', minutes: 30 },
  { label: '60', minutes: 60 }
];

let preferences = loadPreferences();
let customIntervalMinutes = preferences.intervalMs / 60_000;
let customDurationMinutes = preferences.durationMs ? preferences.durationMs / 60_000 : 20;
let session = createSession(preferences.intervalMs, getDurationMs(preferences));
let audioStatus = 'Sound ready after tap';
let lastSignal = 'Waiting for first bell';
let wakeLockStatus = 'Off';
let notificationStatus = notificationCapabilityLabel();
let wakeLockSentinel: WakeLockSentinel | null = null;
let tickHandle = 0;
let visualPulseHandle = 0;
let pulseClass = '';

const appRoot = document.querySelector<HTMLDivElement>('#app');

if (!appRoot) {
  throw new Error('App root missing.');
}

const app = appRoot;

render();
startRenderLoop();

function render(): void {
  const snapshot = getSnapshot(session);
  const selectedSound = getBowlSound(preferences.soundId);
  const isRunning = session.status === 'running';
  const isPaused = session.status === 'paused';
  const isComplete = session.status === 'complete';
  const durationMs = getDurationMs(preferences);
  const nextText =
    isComplete || session.status === 'idle'
      ? formatTime(session.intervalMs)
      : formatTime(snapshot.intervalRemainingMs);
  const sessionText =
    durationMs == null
      ? 'Open session'
      : isComplete
        ? 'Session complete'
        : `${formatTime(snapshot.sessionRemainingMs ?? durationMs)} left`;
  const progressDegrees = Math.round(snapshot.progressRatio * 360);
  const intervalMinutes = session.intervalMs / 60_000;

  app.innerHTML = `
    <section class="timer-app ${pulseClass}" data-status="${session.status}" data-last-signal="${lastSignal}">
      <div class="ambient ambient-one"></div>
      <div class="ambient ambient-two"></div>

      <header class="masthead">
        <p class="eyebrow">Tibetan bowl timer</p>
        <h1>Bell every ${formatMinutes(intervalMinutes)}</h1>
        <p class="subhead">${sessionText}</p>
      </header>

      <section class="timer-face" aria-label="Timer status">
        <div class="ring" style="--progress: ${progressDegrees}deg">
          <div class="time-readout" data-testid="countdown">${nextText}</div>
          <div class="time-label">${isComplete ? 'complete' : isPaused ? 'paused' : isRunning ? 'next bell' : 'ready'}</div>
        </div>
        <p class="signal" data-testid="signal-text">${lastSignal}</p>
      </section>

      <section class="primary-controls" aria-label="Timer controls">
        <button class="control control-primary" data-action="start" ${isRunning ? 'hidden' : ''}>
          ${isPaused ? 'Resume' : isComplete ? 'Start again' : 'Start'}
        </button>
        <button class="control" data-action="pause" ${isRunning ? '' : 'hidden'}>Pause</button>
        <button class="control" data-action="reset">Reset</button>
      </section>

      <section class="panel settings-panel" aria-label="Timer settings">
        <div class="field-group">
          <div class="field-head">
            <label>Interval</label>
            <span>${formatMinutes(intervalMinutes)}</span>
          </div>
          <div class="segmented" role="group" aria-label="Interval presets">
            ${intervalPresets
              .map(
                (preset) => `
                  <button class="${Math.round(minutesToMs(preset.minutes)) === session.intervalMs ? 'selected' : ''}" data-interval="${preset.minutes}">
                    ${preset.label}
                  </button>
                `
              )
              .join('')}
          </div>
          <label class="input-row">
            <span>Custom min</span>
            <input data-input="custom-interval" type="number" min="0.1" max="1440" step="0.1" value="${roundInput(customIntervalMinutes)}" />
          </label>
        </div>

        <div class="field-group">
          <div class="field-head">
            <label>Session</label>
            <span>${durationMs == null ? 'Open' : formatMinutes(durationMs / 60_000)}</span>
          </div>
          <div class="segmented duration-grid" role="group" aria-label="Session duration">
            ${durationPresets
              .map(
                (preset) => `
                  <button class="${durationButtonSelected(preset.minutes) ? 'selected' : ''}" data-duration="${preset.minutes ?? 'open'}">
                    ${preset.label}
                  </button>
                `
              )
              .join('')}
          </div>
          <label class="input-row">
            <span>Custom min</span>
            <input data-input="custom-duration" type="number" min="0.1" max="1440" step="0.1" value="${roundInput(customDurationMinutes)}" ${preferences.durationMode === 'fixed' ? '' : 'disabled'} />
          </label>
        </div>
      </section>

      <section class="panel sound-panel" aria-label="Sound settings">
        <div class="field-head">
          <label>Bowl</label>
          <span>${selectedSound.name}</span>
        </div>
        <div class="sound-list">
          ${BOWL_SOUNDS
            .map(
              (sound) => `
                <button class="sound-card ${sound.id === preferences.soundId ? 'selected' : ''}" data-sound="${sound.id}">
                  <strong>${sound.name}</strong>
                  <span>${sound.description}</span>
                </button>
              `
            )
            .join('')}
        </div>
        <div class="sound-actions">
          <button class="control" data-action="preview">Preview</button>
          <label class="volume">
            <span>Volume</span>
            <input data-input="volume" type="range" min="0" max="1" step="0.01" value="${preferences.volume}" />
          </label>
        </div>
        <p class="status-line" data-testid="audio-status">${audioStatus}</p>
      </section>

      <section class="panel utility-panel" aria-label="Optional alerts">
        <label class="toggle-row">
          <span>
            <strong>Notification</strong>
            <small>${notificationStatus}</small>
          </span>
          <input data-input="notifications" type="checkbox" ${preferences.notificationsEnabled ? 'checked' : ''} />
        </label>
        <label class="toggle-row">
          <span>
            <strong>Wake lock</strong>
            <small>${wakeLockStatus}</small>
          </span>
          <input data-input="wake-lock" type="checkbox" ${preferences.wakeLockEnabled ? 'checked' : ''} />
        </label>
      </section>
    </section>
  `;

  bindEvents();
}

function bindEvents(): void {
  app.querySelectorAll<HTMLButtonElement>('[data-interval]').forEach((button) => {
    button.addEventListener('click', () => {
      const minutes = Number(button.dataset.interval);
      setIntervalMinutes(minutes);
    });
  });

  app.querySelectorAll<HTMLButtonElement>('[data-duration]').forEach((button) => {
    button.addEventListener('click', () => {
      const value = button.dataset.duration;
      if (value === 'open') {
        preferences = { ...preferences, durationMode: 'open', durationMs: null };
      } else {
        setDurationMinutes(Number(value));
      }
      applySettingsChange();
    });
  });

  app.querySelector<HTMLInputElement>('[data-input="custom-interval"]')?.addEventListener('change', (event) => {
    const input = event.currentTarget as HTMLInputElement;
    setIntervalMinutes(Number(input.value));
  });

  app.querySelector<HTMLInputElement>('[data-input="custom-duration"]')?.addEventListener('change', (event) => {
    const input = event.currentTarget as HTMLInputElement;
    setDurationMinutes(Number(input.value));
  });

  app.querySelectorAll<HTMLButtonElement>('[data-sound]').forEach((button) => {
    button.addEventListener('click', () => {
      preferences = { ...preferences, soundId: button.dataset.sound ?? preferences.soundId };
      persistAndRender();
    });
  });

  app.querySelector<HTMLInputElement>('[data-input="volume"]')?.addEventListener('input', (event) => {
    const input = event.currentTarget as HTMLInputElement;
    preferences = { ...preferences, volume: Number(input.value) };
    savePreferences(preferences);
  });

  app.querySelector<HTMLInputElement>('[data-input="notifications"]')?.addEventListener('change', async (event) => {
    const input = event.currentTarget as HTMLInputElement;
    const enabled = input.checked;
    preferences = { ...preferences, notificationsEnabled: enabled };
    if (enabled) {
      await requestNotifications();
    }
    persistAndRender();
  });

  app.querySelector<HTMLInputElement>('[data-input="wake-lock"]')?.addEventListener('change', async (event) => {
    const input = event.currentTarget as HTMLInputElement;
    preferences = { ...preferences, wakeLockEnabled: input.checked };
    await updateWakeLock();
    persistAndRender();
  });

  app.querySelector<HTMLButtonElement>('[data-action="start"]')?.addEventListener('click', async () => {
    await prepareAudio();
    session = session.status === 'paused' ? resumeSession(session) : startSession(session);
    lastSignal = session.status === 'running' ? 'Timer running' : lastSignal;
    await updateWakeLock();
    render();
  });

  app.querySelector<HTMLButtonElement>('[data-action="pause"]')?.addEventListener('click', () => {
    session = pauseSession(session);
    lastSignal = 'Paused';
    void releaseWakeLock();
    render();
  });

  app.querySelector<HTMLButtonElement>('[data-action="reset"]')?.addEventListener('click', () => {
    session = resetSession(session);
    lastSignal = 'Waiting for first bell';
    void releaseWakeLock();
    render();
  });

  app.querySelector<HTMLButtonElement>('[data-action="preview"]')?.addEventListener('click', async () => {
    await prepareAudio();
    lastSignal = 'Preview bell';
    await playSignal('interval');
    render();
  });
}

function startRenderLoop(): void {
  window.clearInterval(tickHandle);
  tickHandle = window.setInterval(() => {
    if (session.status === 'running') {
      handleTick();
      render();
    }
  }, 250);
}

function handleTick(): void {
  const snapshot = getSnapshot(session);
  if (snapshot.isComplete) {
    session = completeSession(session);
    lastSignal = 'Final double bell';
    void playSignal('final');
    void releaseWakeLock();
    render();
    return;
  }

  if (shouldPlayInterval(session, snapshot)) {
    session = markIntervalPlayed(session, snapshot.intervalIndex);
    lastSignal = 'Interval bell';
    void playSignal('interval');
    notify('Interval bell', 'Your bowl interval has arrived.');
  }
}

async function prepareAudio(): Promise<void> {
  try {
    await ensureAudioContext();
    audioStatus = 'Sound ready';
  } catch (error) {
    audioStatus = error instanceof Error ? error.message : 'Sound unavailable';
  }
}

async function playSignal(signal: BellSignal): Promise<void> {
  logBell(signal);
  triggerPulse(signal);
  try {
    await playBellSignal(signal, getBowlSound(preferences.soundId), preferences.volume, (strikeSignal) => {
      if (strikeSignal === 'final') {
        notify('Session complete', 'Your meditation ended with a double bell.');
      }
    });
  } catch (error) {
    audioStatus = error instanceof Error ? error.message : 'Sound unavailable';
  }
}

function triggerPulse(signal: BellSignal): void {
  pulseClass = signal === 'final' ? 'pulse-final' : 'pulse-interval';
  window.clearTimeout(visualPulseHandle);
  visualPulseHandle = window.setTimeout(() => {
    pulseClass = '';
    render();
  }, signal === 'final' ? 1600 : 900);
}

function setIntervalMinutes(minutes: number): void {
  const intervalMs = minutesToMs(minutes);
  customIntervalMinutes = minutes;
  preferences = { ...preferences, intervalMs };
  applySettingsChange();
}

function setDurationMinutes(minutes: number): void {
  const durationMs = minutesToMs(minutes);
  customDurationMinutes = minutes;
  preferences = { ...preferences, durationMode: 'fixed', durationMs };
  applySettingsChange();
}

function applySettingsChange(): void {
  session = updateSessionSettings(session, preferences.intervalMs, getDurationMs(preferences));
  lastSignal = 'Waiting for first bell';
  persistAndRender();
}

function persistAndRender(): void {
  preferences = {
    ...DEFAULT_PREFERENCES,
    ...preferences,
    durationMs: preferences.durationMode === 'fixed' ? preferences.durationMs : null
  };
  savePreferences(preferences);
  render();
}

function getDurationMs(current: UserPreferences): number | null {
  return current.durationMode === 'fixed' ? current.durationMs : null;
}

function durationButtonSelected(minutes: number | null): boolean {
  if (minutes == null) return preferences.durationMode === 'open';
  return preferences.durationMode === 'fixed' && preferences.durationMs === minutesToMs(minutes);
}

function formatMinutes(minutes: number): string {
  if (minutes < 1) return `${Math.round(minutes * 60)} sec`;
  if (Number.isInteger(minutes)) return `${minutes} min`;
  return `${minutes.toFixed(1)} min`;
}

function roundInput(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

async function requestNotifications(): Promise<void> {
  if (!('Notification' in window)) {
    notificationStatus = 'Unsupported';
    preferences = { ...preferences, notificationsEnabled: false };
    return;
  }
  const permission = await Notification.requestPermission();
  notificationStatus = permission === 'granted' ? 'Enabled' : 'Blocked';
  preferences = { ...preferences, notificationsEnabled: permission === 'granted' };
}

function notify(title: string, body: string): void {
  if (!preferences.notificationsEnabled || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  new Notification(title, { body });
}

function notificationCapabilityLabel(): string {
  if (!('Notification' in window)) return 'Unsupported';
  if (Notification.permission === 'granted') return 'Enabled';
  if (Notification.permission === 'denied') return 'Blocked';
  return 'Ask on enable';
}

async function updateWakeLock(): Promise<void> {
  if (!preferences.wakeLockEnabled) {
    await releaseWakeLock();
    wakeLockStatus = 'Off';
    return;
  }

  if (!('wakeLock' in navigator)) {
    wakeLockStatus = 'Unsupported';
    preferences = { ...preferences, wakeLockEnabled: false };
    return;
  }

  try {
    wakeLockSentinel = await navigator.wakeLock.request('screen');
    wakeLockStatus = 'Keeping screen awake';
    wakeLockSentinel.addEventListener('release', () => {
      wakeLockStatus = 'Released';
      render();
    });
  } catch {
    wakeLockStatus = 'Unavailable';
    preferences = { ...preferences, wakeLockEnabled: false };
  }
}

async function releaseWakeLock(): Promise<void> {
  if (wakeLockSentinel) {
    await wakeLockSentinel.release();
    wakeLockSentinel = null;
  }
}

function logBell(signal: BellSignal): void {
  window.__bellLog = window.__bellLog ?? [];
  window.__bellLog.push({ signal, at: Date.now() });
}

declare global {
  interface Window {
    __bellLog?: Array<{ signal: BellSignal; at: number }>;
  }
}
